/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class CustomReportsAPI extends ApiClient {
  constructor() {
    super('custom_reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getCustomAgentOverviewReports({
    since,
    until,
    businessHours,
    selectedLabels,
    selectedInbox,
  } = {}) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      timezone_offset: getTimeOffset(),
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/agents_overview`, { params });
  }

  getCustomAgentConversationStatesReports({
    since,
    until,
    businessHours,
    selectedLabels,
    selectedInbox,
  } = {}) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      timezone_offset: getTimeOffset(),
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/agent_wise_conversation_states`, { params });
  }

  getCustomLabelConversationStatesReports({
    since,
    until,
    businessHours,
    selectedLabels,
    selectedInbox,
  } = {}) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      timezone_offset: getTimeOffset(),
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/label_wise_conversation_states`, { params });
  }

  getCustomAttributeConversationStatesReports({
    since,
    until,
    businessHours,
    attributeKey,
    attributeModel,
    selectedInbox,
  } = {}) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      attribute_key: attributeKey,
      attribute_model: attributeModel,
      timezone_offset: getTimeOffset(),
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    return axios.get(`${this.url}/custom_attribute_wise_conversation_states`, {
      params,
    });
  }

  getCustomAttributeAgentStatesReports({
    since,
    until,
    businessHours,
    attributeKey,
    attributeModel,
    selectedInbox,
  } = {}) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      attribute_key: attributeKey,
      attribute_model: attributeModel,
      timezone_offset: getTimeOffset(),
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    return axios.get(`${this.url}/custom_attribute_wise_agent_states`, {
      params,
    });
  }

  getBotFlows() {
    return axios.get(`${this.url}/bot_flows`);
  }

  getCustomBotAnalyticsOverviewReports({
    since,
    until,
    selectedLabels,
    flowId,
  }) {
    const params = {
      since,
      until,
      business_hours: false,
      timezone_offset: getTimeOffset(),
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    if (flowId) {
      params.flow_id = flowId;
    }

    return axios.get(`${this.url}/bot_analytics_overview`, { params });
  }

  getCustomLiveChatAnalyticsOverviewReports({ since, until, selectedLabels }) {
    const params = {
      since,
      until,
      business_hours: false,
      timezone_offset: getTimeOffset(),
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/live_chat_analytics_overview`, { params });
  }

  getCustomLiveChatOtherMetricsReports({ since, until, selectedLabels }) {
    const params = {
      since,
      until,
      business_hours: false,
      timezone_offset: getTimeOffset(),
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/live_chat_other_metrics_overview`, {
      params,
    });
  }

  getCustomBotAnalyticsSalesOverviewReports({ since, until, selectedLabels }) {
    const params = {
      since,
      until,
      business_hours: false,
      timezone_offset: getTimeOffset(),
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/bot_analytics_sales_overview`, { params });
  }

  getCustomLiveChatSalesOverviewReports({ since, until, selectedLabels }) {
    const params = {
      since,
      until,
      business_hours: false,
      timezone_offset: getTimeOffset(),
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/live_chat_analytics_sales_overview`, {
      params,
    });
  }

  getCustomBotAnalyticsSupportOverviewReports({
    since,
    until,
    selectedLabels,
  }) {
    const params = {
      since,
      until,
      business_hours: false,
      timezone_offset: getTimeOffset(),
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/bot_analytics_support_overview`, { params });
  }

  getCustomLiveChatSupportOverviewReports({ since, until, selectedLabels }) {
    const params = {
      since,
      until,
      business_hours: false,
      timezone_offset: getTimeOffset(),
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/live_chat_analytics_support_overview`, {
      params,
    });
  }

  getCurrency() {
    return axios.get(`${this.url}/shop_currency`);
  }

  getLiveChatCurrency() {
    return axios.get(`${this.url}/live_chat_shop_currency`);
  }

  downloadCustomAgentOverviewReports({
    since,
    until,
    businessHours,
    selectedLabels,
    selectedInbox,
    metricType,
    email,
  }) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      metric_type: metricType,
      email,
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.post(`${this.url}/download_agents_overview`, { ...params });
  }

  downloadCustomAgentWiseConversationStatesReports({
    since,
    until,
    businessHours,
    selectedLabels,
    selectedInbox,
    metricType,
    email,
  }) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      metric_type: metricType,
      email,
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.post(`${this.url}/download_agent_wise_conversation_states`, {
      ...params,
    });
  }

  downloadCustomBotAnalyticsOverviewReports({
    since,
    until,
    selectedLabels,
    email,
  }) {
    const params = {
      since,
      until,
      email,
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.post(`${this.url}/download_bot_analytics_overview`, {
      ...params,
    });
  }

  downloadCustomBotAnalyticsSalesOverviewReports({
    since,
    until,
    selectedLabels,
    email,
  }) {
    const params = {
      since,
      until,
      email,
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.post(`${this.url}/download_bot_analytics_sales_overview`, {
      ...params,
    });
  }

  downloadCustomLiveChatAnalyticsSalesOverviewReports({
    since,
    until,
    selectedLabels,
    email,
  }) {
    const params = {
      since,
      until,
      email,
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.post(
      `${this.url}/download_live_chat_analytics_sales_overview`,
      {
        ...params,
      }
    );
  }

  downloadCustomBotAnalyticsSupportOverviewReports({
    since,
    until,
    selectedLabels,
    email,
  }) {
    const params = {
      since,
      until,
      email,
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.post(`${this.url}/download_bot_analytics_support_overview`, {
      ...params,
    });
  }

  downloadCustomLiveChatAnalyticsSupportOverviewReports({
    since,
    until,
    selectedLabels,
    email,
  }) {
    const params = {
      since,
      until,
      email,
    };

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.post(
      `${this.url}/download_live_chat_analytics_support_overview`,
      {
        ...params,
      }
    );
  }

  getCustomAgentCallOverviewReports({
    since,
    until,
    businessHours,
    selectedLabels,
    selectedInbox,
  } = {}) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      timezone_offset: getTimeOffset(),
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/agent_call_overview`, { params });
  }

  getCustomAgentInboundCallOverviewReports({
    since,
    until,
    businessHours,
    selectedLabels,
    selectedInbox,
  } = {}) {
    const params = {
      since,
      until,
      business_hours: businessHours,
      timezone_offset: getTimeOffset(),
    };

    if (selectedInbox) {
      params.inboxes = [selectedInbox.id];
    }

    if (selectedLabels && selectedLabels.length) {
      params.labels = selectedLabels.map(label => label.title);
    }

    return axios.get(`${this.url}/agent_inbound_call_overview`, { params });
  }

  downloadOverviewReports({ since, until: untilDate, reportTypes, emails }) {
    return axios.post(`${this.url}/download_overview_reports`, {
      since,
      until: untilDate,
      report_types: reportTypes,
      emails,
    });
  }

  getAIResponseAnalytics({ since, until: untilDate, type, mode } = {}) {
    return axios.get(`${this.url}/ai_response_analytics`, {
      params: {
        since,
        until: untilDate,
        type: type || 'ALL',
        mode: mode || 'AGGREGATE',
      },
    });
  }

  getCallLogsReport({ startDate, endDate, email } = {}) {
    return axios.post(`/api/v1/call_logs/export_call_report`, {
      startDate,
      endDate,
      account_id: this.accountIdFromRoute,
      email,
    });
  }

  downloadCallNudgeConversations({ since, until: untilDate, email } = {}) {
    return axios.post(`${this.url}/download_call_nudge_conversations`, {
      since,
      until: untilDate,
      email,
    });
  }
}

export default new CustomReportsAPI();
